// React related imports
import { useMemo, useState } from 'react';

// Components
import { Button, Header, Search } from '@GxT-Team/gxt-components';
import Layout from '../../components/layout';
import SvgIcon from '../../components/elements/SvgIcon';
import { PaginatedTable } from '../../components/PaginatedTable';
import {
  BaseItem,
  PaginatedTableProps,
} from '../../components/PaginatedTable/PaginatedTable.types';

// Hooks and Contexts
import { usePaginatedFilters } from '../../hooks/usePaginatedFilters';
import useModal from '../../hooks/useModal';

// Services and APIs
import { getDataSource } from '../../services/api/dataSource/dataSource';

// Utilities
import { generateColumns } from './components/utils/DataSourceColumns';

// Types
import { ProjectFilterParams, ProjectSortParams } from '../../services/api/types';
import AddDataSourceModal from './components/utils/DataSourceModal/AddDataSourceModal';
import { useDataSource } from '../../services/api/dataSource/useDataSourceAPI';
import { DataSourceTabs, ISqlQueryObj, DataSourceFormData } from './types';
import { getDataSourceMenuItems } from './components/utils/helper';
import { DataSourceObj } from '../../services/api/dataSource/dataSource.types';

const DataSource = () => {
  const {
    setSearchByKey,
    paginatedFilteredData,
    handlePageChange,
    handlePerPageChange,
    handleColumnSort,
  } = usePaginatedFilters<ProjectFilterParams, ProjectSortParams>(
    useMemo(
      () => ({
        search: {
          query: '',
          columns: ['id', 'name'],
        },
        sort: { type: 'DESC', column: 'id' },
      }),
      [],
    ),
  );

  const [selectedDataSource, setSelectedDataSource] = useState<ISqlQueryObj>();

  const { openModal, closeModal, show } = useModal();
  const { addDataSourceQuery, updateDataSourceQuery } = useDataSource();

  const sortedColumn = useMemo(
    () => ({
      column: paginatedFilteredData.sort?.column || '',
      order: paginatedFilteredData.sort?.type || 'DESC',
    }),
    [paginatedFilteredData.sort?.column, paginatedFilteredData.sort?.type],
  );

  const columns = useMemo(
    () => generateColumns(handleColumnSort, sortedColumn),
    [handleColumnSort, sortedColumn],
  );
  const getFilteredPaginatedDataSource = () => {
    return getDataSource(paginatedFilteredData as unknown as DataSourceObj);
  };

  const modalData = async (data: DataSourceFormData) => {
    const { val: _val, ...formData } = data;
    if (selectedDataSource) {
      await updateDataSourceQuery.mutateAsync({
        ...formData,
        id: selectedDataSource.id,
      } as DataSourceObj);
      setSelectedDataSource(undefined);
    } else {
      await addDataSourceQuery.mutateAsync(formData as DataSourceObj);
    }
  };

  const handleEditClick = (row: ISqlQueryObj) => {
    setSelectedDataSource(row);
    openModal();
  };

  const menuItems = getDataSourceMenuItems(handleEditClick);

  return (
    <Layout>
      <Layout.Header>
        <Header>
          <div className="flex w-full justify-between">
            <Header.Heading>Data Source</Header.Heading>
            <Button
              variant="primary"
              onClick={openModal}
              data-testid="filter-btn"
              className="flex justify-between items-center gap-3 whitespace-nowrap !h-10">
              <SvgIcon date-testid={'add-icon-svg'} iconType={'add'} className="!text-white" />
              {`Add Data Source`}
            </Button>
          </div>
        </Header>
      </Layout.Header>
      <Layout.Body>
        <div className="flex w-full gap-2">
          <Search
            onChangeCallback={(term) => {
              setSearchByKey('query', term);
            }}
            width="100%"
            className="h-full"
          />
        </div>
        <PaginatedTable
          additionalColumns={columns}
          paginatedFilteredData={paginatedFilteredData}
          getData={
            getFilteredPaginatedDataSource as unknown as PaginatedTableProps<BaseItem>['getData']
          }
          isCheckboxRequired={false}
          handlePageChange={handlePageChange}
          handlePerPageChange={handlePerPageChange}
          handleSelectAll={undefined}
          queryKey={['dataSource', DataSourceTabs.DATA_SOURCE]}
          label="dataSource"
          actionsColumn
          menuItems={menuItems}
        />

        <AddDataSourceModal
          isOpen={show}
          closeModal={closeModal}
          execFunction={modalData}
          selectedDataSource={selectedDataSource}
          setSelectedDataSource={setSelectedDataSource}
        />
      </Layout.Body>
    </Layout>
  );
};

export default DataSource;
